/**
 * Custom compounds reactive store.
 *
 * Manages user-defined compound materials stored in localStorage with
 * full CRUD operations, validation, and integration with entity selection.
 *
 * @packageDocumentation
 */

import { browser } from "$app/environment";
import type { CustomCompound } from "$lib/wasm/types";

/** Compound element with atomic number and atom count */
export interface CompoundElementEntry {
  atomicNumber: number;
  atomCount: number;
}

/**
 * Merge two rows of the same element, summing their atom counts.
 * Throws if the atomic numbers do not match.
 */
export function mergeRows(
  rowA: CompoundElementEntry,
  rowB: CompoundElementEntry,
): CompoundElementEntry {
  if (rowA.atomicNumber !== rowB.atomicNumber) {
    throw new Error(
      `Cannot merge different elements: Z=${rowA.atomicNumber} and Z=${rowB.atomicNumber}`,
    );
  }
  return {
    atomicNumber: rowA.atomicNumber,
    atomCount: rowA.atomCount + rowB.atomCount,
  };
}

/**
 * Stored compound with full metadata.
 * This is the internal storage format.
 */
export interface StoredCompoundInternal {
  /** Stable opaque identifier (cc_<uuidv7>) */
  id: string;
  /** Display name in material selector */
  name: string;
  /** Normalized name for duplicate detection (trim + lowercase) */
  normalizedName: string;
  /** Elemental composition */
  elements: CompoundElementEntry[];
  /** Material density in g/cm³ */
  density: number;
  /** Optional mean excitation potential in eV */
  iValue?: number | undefined;
  /** Aggregate phase for default unit selection */
  phase: "gas" | "condensed";
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
  /** Last edit timestamp (ISO 8601) */
  updatedAt: string;
}

/** localStorage envelope with schema version */
interface CustomCompoundStoreEnvelope {
  schemaVersion: 1;
  compounds: StoredCompoundInternal[];
}

const STORAGE_KEY = "customCompounds";
const LIBRARY_SOFT_LIMIT = 50;
const LIBRARY_WARNING_THRESHOLD = 45;

/** localStorage adapter with browser guard */
const storage = {
  get(): CustomCompoundStoreEnvelope {
    if (!browser) {
      return { schemaVersion: 1, compounds: [] };
    }

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { schemaVersion: 1, compounds: [] };
    }

    try {
      const parsed = JSON.parse(raw) as CustomCompoundStoreEnvelope | StoredCompoundInternal[];

      // Handle legacy array format (pre-v1 schema)
      if (Array.isArray(parsed)) {
        return { schemaVersion: 1, compounds: parsed };
      }

      // Validate schema version
      if (parsed.schemaVersion === 1) {
        return parsed;
      }

      // Unknown schema version - return empty and log warning
      console.warn(
        `custom-compounds: Unknown schema version ${parsed.schemaVersion}, starting fresh`,
      );
      return { schemaVersion: 1, compounds: [] };
    } catch (err) {
      console.error("custom-compounds: Failed to parse localStorage data", err);
      return { schemaVersion: 1, compounds: [] };
    }
  },

  set(envelope: CustomCompoundStoreEnvelope): boolean {
    if (!browser) {
      return false;
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
      return true;
    } catch (err) {
      // localStorage quota exceeded
      console.error("custom-compounds: localStorage quota exceeded", err);
      return false;
    }
  },
};

/** Generate a stable ID with cc_ prefix and uuidv7-style identifier */
function generateCompoundId(): string {
  const cryptoApi = typeof globalThis.crypto === "object" ? globalThis.crypto : undefined;
  if (cryptoApi?.randomUUID) {
    return `cc_${cryptoApi.randomUUID()}`;
  }
  const values = new Uint32Array(4);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(values);
  } else {
    values[0] = Date.now();
    values[1] = typeof performance !== "undefined" ? performance.now() * 1000 : Date.now() >>> 8;
    values[2] = Math.floor(Math.random() * 0xffffffff);
    values[3] = Math.floor(Math.random() * 0xffffffff);
  }
  return `cc_${Array.from(values, (value) => value.toString(16).padStart(8, "0")).join("")}`;
}

function generateTransientCompoundId(): string {
  return generateCompoundId().replace(/^cc_/, "cc_url_");
}

/** Normalize name for duplicate detection */
function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Suggest a library-unique display name by appending `(copy)`, `(copy 2)`, …
 * Used when copying a shared/transient compound into the local library so the
 * receiver never silently overwrites an existing entry with the same name.
 *
 * `Foo` → `Foo (copy)` → `Foo (copy 2)` → …
 */
export function suggestCopyName(baseName: string, isTaken: (name: string) => boolean): string {
  const trimmed = baseName.trim();
  if (!isTaken(trimmed)) return trimmed;
  let candidate = `${trimmed} (copy)`;
  let n = 2;
  while (isTaken(candidate)) {
    candidate = `${trimmed} (copy ${n})`;
    n++;
  }
  return candidate;
}

/** Validate compound data before saving */
export interface CompoundValidationError {
  field: "name" | "density" | "iValue" | "elements" | "general";
  message: string;
}

export function validateCompound(params: {
  name: string;
  density: number;
  iValue?: number | undefined;
  elements: Array<{ atomicNumber: number; atomCount: number }>;
  phase: "gas" | "condensed";
}): CompoundValidationError[] {
  const errors: CompoundValidationError[] = [];

  // Name validation
  const trimmedName = params.name.trim();
  if (!trimmedName) {
    errors.push({ field: "name", message: "Name is required." });
  } else if (trimmedName.length > 80) {
    errors.push({ field: "name", message: "Name must be 80 characters or fewer." });
  }

  // Density validation
  if (params.density === undefined || params.density === null || !Number.isFinite(params.density)) {
    errors.push({ field: "density", message: "Density is required." });
  } else if (params.density <= 0) {
    errors.push({ field: "density", message: "Density must be greater than zero." });
  } else if (params.density > 25) {
    errors.push({ field: "density", message: "Density must be ≤ 25 g/cm³." });
  }

  // I-value validation (optional)
  if (params.iValue !== undefined && params.iValue !== null) {
    if (!Number.isFinite(params.iValue) || params.iValue <= 0) {
      errors.push({ field: "iValue", message: "I-value must be a positive number." });
    } else if (params.iValue > 10000) {
      errors.push({ field: "iValue", message: "I-value must be ≤ 10 000 eV." });
    }
  }

  // Elements validation
  if (!params.elements || !Array.isArray(params.elements) || params.elements.length === 0) {
    errors.push({ field: "elements", message: "At least one element is required." });
  } else {
    const seenZ = new Set<number>();
    for (const element of params.elements) {
      // Validate atomic number range
      if (element.atomicNumber < 1 || element.atomicNumber > 118) {
        errors.push({
          field: "elements",
          message: `Unknown element: Z=${element.atomicNumber}.`,
        });
      }

      // Check for duplicates
      if (seenZ.has(element.atomicNumber)) {
        errors.push({
          field: "elements",
          message: `Element Z=${element.atomicNumber} is listed more than once. Combine into a single row.`,
        });
      }
      seenZ.add(element.atomicNumber);

      // Validate atom count
      if (element.atomCount <= 0) {
        errors.push({ field: "elements", message: "Count must be greater than zero." });
      } else if (element.atomCount > 1000) {
        errors.push({ field: "elements", message: "Atom count must be ≤ 1000." });
      }
    }
  }

  return errors;
}

/** Custom compounds store state and operations */
export interface CustomCompoundsStore {
  /** All stored compounds (reactive) */
  compounds: StoredCompoundInternal[];
  /** Count of compounds (reactive, useful for limit warnings) */
  count: number;
  /** Whether library has reached warning threshold */
  hasReachedWarningThreshold: boolean;
  /** Whether library is at soft limit */
  isAtLimit: boolean;

  /** Get a compound by ID */
  getById(id: string): StoredCompoundInternal | undefined;
  /** Get compound by index */
  get(index: number): StoredCompoundInternal | undefined;

  /** Add a session-only compound reconstructed from a shared URL. */
  addTransient(params: {
    name: string;
    density: number;
    iValue?: number | undefined;
    elements: Array<{ atomicNumber: number; atomCount: number }>;
    phase: "gas" | "condensed";
  }): StoredCompoundInternal;

  /** Remove a session-only compound. */
  removeTransient(id: string): boolean;

  /** Whether a compound id is session-only and not persisted in localStorage. */
  isTransient(id: string): boolean;

  /**
   * Prepare an editable library copy of a transient/shared compound. Returns the
   * source fields with a library-unique name (see {@link suggestCopyName}); does
   * NOT persist. The caller opens the editor pre-filled with this and persists
   * via {@link CustomCompoundsStore.create} on Save.
   */
  editAndSaveCopy(source: {
    name: string;
    density: number;
    iValue?: number | undefined;
    elements: Array<{ atomicNumber: number; atomCount: number }>;
    phase: "gas" | "condensed";
  }): {
    name: string;
    density: number;
    iValue?: number | undefined;
    elements: CompoundElementEntry[];
    phase: "gas" | "condensed";
  };

  /**
   * Create a new compound.
   * @returns Success status and either compound or validation errors
   */
  create(params: {
    name: string;
    density: number;
    iValue?: number | undefined;
    elements: Array<{ atomicNumber: number; atomCount: number }>;
    phase: "gas" | "condensed";
  }):
    | { success: true; compound: StoredCompoundInternal }
    | { success: false; errors: CompoundValidationError[] };

  /**
   * Update an existing compound.
   * @returns Success status and either compound or validation errors
   */
  update(
    id: string,
    params: {
      name: string;
      density: number;
      iValue?: number | undefined;
      elements: Array<{ atomicNumber: number; atomCount: number }>;
      phase: "gas" | "condensed";
    },
  ):
    | { success: true; compound: StoredCompoundInternal }
    | { success: false; errors: CompoundValidationError[] };

  /** Delete a compound by ID. Returns true if found and deleted. */
  delete(id: string): boolean;

  /** Check if a name already exists (case-insensitive) */
  nameExists(name: string, excludeId?: string): boolean;

  /** Export all compounds as JSON for backup/transfer */
  export(): string;

  /** Import compounds from JSON (merges with existing) */
  import(json: string): { success: boolean; count: number; error?: string };
}

/** Create the custom compounds store */
export function createCustomCompoundsStore(): CustomCompoundsStore {
  const initial = storage.get();

  const compounds = $state<StoredCompoundInternal[]>(initial.compounds);
  const transientCompounds = $state<StoredCompoundInternal[]>([]);
  const version = $state({ count: 0 });

  const store: CustomCompoundsStore = {
    get compounds(): StoredCompoundInternal[] {
      void version.count;
      return [...compounds, ...transientCompounds];
    },

    get count(): number {
      void version.count;
      return compounds.length;
    },

    get hasReachedWarningThreshold(): boolean {
      void version.count;
      return compounds.length >= LIBRARY_WARNING_THRESHOLD;
    },

    get isAtLimit(): boolean {
      void version.count;
      return compounds.length >= LIBRARY_SOFT_LIMIT;
    },

    getById(id: string): StoredCompoundInternal | undefined {
      void version.count;
      return compounds.find((c) => c.id === id) ?? transientCompounds.find((c) => c.id === id);
    },

    get(index: number): StoredCompoundInternal | undefined {
      void version.count;
      return [...compounds, ...transientCompounds][index];
    },

    addTransient(params): StoredCompoundInternal {
      const now = new Date().toISOString();
      const normalizedName = normalizeName(params.name);
      const compound: StoredCompoundInternal = {
        id: generateTransientCompoundId(),
        name: params.name.trim(),
        normalizedName,
        elements: [...params.elements].sort((a, b) => a.atomicNumber - b.atomicNumber),
        density: params.density,
        iValue: params.iValue,
        phase: params.phase,
        createdAt: now,
        updatedAt: now,
      };
      transientCompounds.push(compound);
      version.count++;
      return compound;
    },

    removeTransient(id: string): boolean {
      const index = transientCompounds.findIndex((c) => c.id === id);
      if (index === -1) return false;
      transientCompounds.splice(index, 1);
      version.count++;
      return true;
    },

    isTransient(id: string): boolean {
      void version.count;
      return transientCompounds.some((c) => c.id === id);
    },

    editAndSaveCopy(source): {
      name: string;
      density: number;
      iValue?: number | undefined;
      elements: CompoundElementEntry[];
      phase: "gas" | "condensed";
    } {
      const name = suggestCopyName(source.name, (n) => this.nameExists(n));
      return {
        name,
        density: source.density,
        iValue: source.iValue,
        elements: source.elements.map((e) => ({ ...e })),
        phase: source.phase,
      };
    },

    create(
      params,
    ):
      | { success: true; compound: StoredCompoundInternal }
      | { success: false; errors: CompoundValidationError[] } {
      const errors = validateCompound(params);
      if (errors.length > 0) {
        return { success: false, errors };
      }

      const now = new Date().toISOString();
      const normalizedName = normalizeName(params.name);

      const compound: StoredCompoundInternal = {
        id: generateCompoundId(),
        name: params.name.trim(),
        normalizedName,
        elements: [...params.elements].sort((a, b) => a.atomicNumber - b.atomicNumber),
        density: params.density,
        iValue: params.iValue,
        phase: params.phase,
        createdAt: now,
        updatedAt: now,
      };

      compounds.push(compound);
      version.count++;

      const envelope: CustomCompoundStoreEnvelope = { schemaVersion: 1, compounds };
      storage.set(envelope);

      return { success: true, compound };
    },

    update(
      id: string,
      params,
    ):
      | { success: true; compound: StoredCompoundInternal }
      | { success: false; errors: CompoundValidationError[] } {
      const existing = compounds.find((c) => c.id === id);
      if (!existing) {
        return {
          success: false,
          errors: [{ field: "general", message: "Compound not found." }],
        };
      }

      const errors = validateCompound(params);
      if (errors.length > 0) {
        return { success: false, errors };
      }

      let now = new Date().toISOString();
      while (now === existing.createdAt) {
        now = new Date(Date.now() + 1).toISOString();
      }
      const normalizedName = normalizeName(params.name);

      existing.name = params.name.trim();
      existing.normalizedName = normalizedName;
      existing.elements = [...params.elements].sort((a, b) => a.atomicNumber - b.atomicNumber);
      existing.density = params.density;
      existing.iValue = params.iValue;
      existing.phase = params.phase;
      existing.updatedAt = now;

      version.count++;

      const envelope: CustomCompoundStoreEnvelope = { schemaVersion: 1, compounds };
      storage.set(envelope);

      return { success: true, compound: existing };
    },

    delete(id: string): boolean {
      const index = compounds.findIndex((c) => c.id === id);
      if (index === -1) {
        return this.removeTransient(id);
      }

      compounds.splice(index, 1);
      version.count++;

      const envelope: CustomCompoundStoreEnvelope = { schemaVersion: 1, compounds };
      storage.set(envelope);
      return true;
    },

    nameExists(name: string, excludeId?: string): boolean {
      const normalized = normalizeName(name);
      return compounds.some((c) => c.normalizedName === normalized && c.id !== excludeId);
    },

    export(): string {
      const envelope: CustomCompoundStoreEnvelope = { schemaVersion: 1, compounds };
      return JSON.stringify(envelope, null, 2);
    },

    import(json: string): { success: boolean; count: number; error?: string } {
      try {
        const parsed = JSON.parse(json);
        let newCompounds: StoredCompoundInternal[];

        if (
          parsed &&
          typeof parsed === "object" &&
          "schemaVersion" in parsed &&
          "compounds" in parsed
        ) {
          newCompounds = (parsed as CustomCompoundStoreEnvelope).compounds;
        } else if (Array.isArray(parsed)) {
          newCompounds = parsed;
        } else {
          return { success: false, count: 0, error: "Invalid import format" };
        }

        let imported = 0;
        for (const compound of newCompounds) {
          if (!compound.id || !compound.name || !compound.elements || !compound.density) {
            continue;
          }

          if (compounds.some((c) => c.id === compound.id)) {
            continue;
          }

          const toImport: StoredCompoundInternal = {
            id: compound.id,
            name: compound.name,
            normalizedName: compound.normalizedName ?? normalizeName(compound.name),
            elements: compound.elements,
            density: compound.density,
            iValue: compound.iValue,
            phase: compound.phase ?? "condensed",
            createdAt: compound.createdAt ?? new Date().toISOString(),
            updatedAt: compound.updatedAt ?? new Date().toISOString(),
          };

          compounds.push(toImport);
          imported++;
        }

        version.count++;
        const envelope: CustomCompoundStoreEnvelope = { schemaVersion: 1, compounds };
        storage.set(envelope);
        return { success: true, count: imported };
      } catch (err) {
        return {
          success: false,
          count: 0,
          error: err instanceof Error ? err.message : "Failed to parse JSON",
        };
      }
    },
  };

  return store;
}

/**
 * Singleton store instance (created on first access).
 * Use this for accessing the store in components.
 */
export const customCompounds = /* @__PURE__ */ createCustomCompoundsStore();

/**
 * Convert a stored compound to the WASM CustomCompound interface.
 * This strips metadata and returns only the calculation-relevant fields.
 */
export function toCustomCompoundInput(compound: StoredCompoundInternal): CustomCompound {
  return {
    name: compound.name,
    elements: compound.elements.map((e) => ({
      atomicNumber: e.atomicNumber,
      fraction: e.atomCount,
      type: "atomic" as const,
    })),
    density: compound.density,
    iValue: compound.iValue,
  };
}
